import { recordAppError } from "@shared/utils/errors/errorLogger";
import { Centrifuge, PublicationContext, Subscription } from "centrifuge";
import { useCallback, useEffect, useReducer, useRef } from "react";
import {
  CENTRIFUGE_EVENTS,
  CENTRIFUGE_SUBSCRIPTION_EVENTS,
  REALTIME_CONNECTION_ACTIONS,
  REALTIME_CONNECTION_ERROR_MESSAGES,
  REALTIME_CONNECTION_STATES,
} from "../constants/realtime-connection-constants";
import { RealtimeConnectionInfo } from "../data/types/realtime";

type UseRealtimeConnectionOptions<T> = {
  url: string;
  channel: string | undefined;
  enabled: boolean;
  reconnectDelayMs?: number;
  maxReconnectDelayMs?: number;
  parseMessage: (data: Record<string, unknown>) => T | null;
  onMessage?: (message: T) => void;
};

export type UseRealtimeConnectionReturn = {
  connectionInfo: RealtimeConnectionInfo;
  disconnect: () => void;
};

type ConnectionStateAction =
  | { type: typeof REALTIME_CONNECTION_ACTIONS.RESET }
  | { type: typeof REALTIME_CONNECTION_ACTIONS.CONNECTING }
  | { type: typeof REALTIME_CONNECTION_ACTIONS.CONNECTED }
  | { type: typeof REALTIME_CONNECTION_ACTIONS.MESSAGE_RECEIVED }
  | { type: typeof REALTIME_CONNECTION_ACTIONS.RECONNECTING }
  | { type: typeof REALTIME_CONNECTION_ACTIONS.DISCONNECTED }
  | { type: typeof REALTIME_CONNECTION_ACTIONS.ERROR; error: string };

const INITIAL_CONNECTION_INFO: RealtimeConnectionInfo = {
  state: REALTIME_CONNECTION_STATES.DISCONNECTED,
  reconnectAttempts: 0,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function connectionInfoReducer(
  state: RealtimeConnectionInfo,
  action: ConnectionStateAction
): RealtimeConnectionInfo {
  switch (action.type) {
    case REALTIME_CONNECTION_ACTIONS.RESET:
      return INITIAL_CONNECTION_INFO;
    case REALTIME_CONNECTION_ACTIONS.CONNECTING:
      return {
        ...state,
        state: REALTIME_CONNECTION_STATES.CONNECTING,
        lastError: undefined,
      };
    case REALTIME_CONNECTION_ACTIONS.CONNECTED:
      return {
        state: REALTIME_CONNECTION_STATES.CONNECTED,
        reconnectAttempts: 0,
      };
    case REALTIME_CONNECTION_ACTIONS.MESSAGE_RECEIVED:
      return {
        ...state,
        state: REALTIME_CONNECTION_STATES.CONNECTED,
        lastError: undefined,
      };
    case REALTIME_CONNECTION_ACTIONS.RECONNECTING:
      return {
        ...state,
        state: REALTIME_CONNECTION_STATES.RECONNECTING,
        reconnectAttempts: state.reconnectAttempts + 1,
      };
    case REALTIME_CONNECTION_ACTIONS.DISCONNECTED:
      return {
        ...state,
        state: REALTIME_CONNECTION_STATES.DISCONNECTED,
      };
    case REALTIME_CONNECTION_ACTIONS.ERROR:
      return {
        ...state,
        state: REALTIME_CONNECTION_STATES.ERROR,
        lastError: action.error,
      };
    default:
      return state;
  }
}

export function useRealtimeConnection<T>({
  url,
  channel,
  enabled,
  reconnectDelayMs = 2000,
  maxReconnectDelayMs = 30000,
  parseMessage,
  onMessage,
}: UseRealtimeConnectionOptions<T>): UseRealtimeConnectionReturn {
  const [connectionInfo, dispatch] = useReducer(
    connectionInfoReducer,
    INITIAL_CONNECTION_INFO
  );
  const centrifugeRef = useRef<Centrifuge | null>(null);
  const subscriptionRef = useRef<Subscription | null>(null);
  const hasConnectedRef = useRef(false);
  const onMessageRef = useRef(onMessage);

  onMessageRef.current = onMessage;

  const disconnect = useCallback(() => {
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }

    if (centrifugeRef.current) {
      centrifugeRef.current.disconnect();
      centrifugeRef.current = null;
    }

    hasConnectedRef.current = false;
    dispatch({ type: REALTIME_CONNECTION_ACTIONS.RESET });
  }, []);

  const handlePublication = useCallback(
    (ctx: PublicationContext): void => {
      if (!isRecord(ctx.data)) {
        recordAppError(
          new Error(REALTIME_CONNECTION_ERROR_MESSAGES.PARSE_MESSAGE)
        );
        dispatch({
          type: REALTIME_CONNECTION_ACTIONS.ERROR,
          error: REALTIME_CONNECTION_ERROR_MESSAGES.PARSE_MESSAGE,
        });
        return;
      }

      const message = parseMessage(ctx.data);
      if (!message) {
        recordAppError(
          new Error(REALTIME_CONNECTION_ERROR_MESSAGES.PARSE_MESSAGE)
        );
        dispatch({
          type: REALTIME_CONNECTION_ACTIONS.ERROR,
          error: REALTIME_CONNECTION_ERROR_MESSAGES.PARSE_MESSAGE,
        });
        return;
      }

      dispatch({ type: REALTIME_CONNECTION_ACTIONS.MESSAGE_RECEIVED });
      onMessageRef.current?.(message);
    },
    [parseMessage]
  );

  const connect = useCallback((): void => {
    if (typeof window === "undefined" || !channel || centrifugeRef.current) {
      return;
    }

    dispatch({ type: REALTIME_CONNECTION_ACTIONS.CONNECTING });

    try {
      const centrifuge = new Centrifuge(url, {
        minReconnectDelay: reconnectDelayMs,
        maxReconnectDelay: maxReconnectDelayMs,
      });

      centrifuge.on(CENTRIFUGE_EVENTS.CONNECTED, () => {
        hasConnectedRef.current = true;
        dispatch({ type: REALTIME_CONNECTION_ACTIONS.CONNECTED });
      });

      centrifuge.on(CENTRIFUGE_EVENTS.DISCONNECTED, () => {
        dispatch({ type: REALTIME_CONNECTION_ACTIONS.DISCONNECTED });
      });

      centrifuge.on(CENTRIFUGE_EVENTS.CONNECTING, () => {
        if (!hasConnectedRef.current) {
          dispatch({ type: REALTIME_CONNECTION_ACTIONS.CONNECTING });
          return;
        }

        dispatch({ type: REALTIME_CONNECTION_ACTIONS.RECONNECTING });
      });

      centrifuge.on(CENTRIFUGE_EVENTS.ERROR, (ctx) => {
        dispatch({
          type: REALTIME_CONNECTION_ACTIONS.ERROR,
          error:
            ctx.error?.message || REALTIME_CONNECTION_ERROR_MESSAGES.CONNECTION,
        });
      });

      const subscription = centrifuge.newSubscription(channel);

      subscription.on(
        CENTRIFUGE_SUBSCRIPTION_EVENTS.PUBLICATION,
        handlePublication
      );
      subscription.on(CENTRIFUGE_SUBSCRIPTION_EVENTS.ERROR, (ctx) => {
        dispatch({
          type: REALTIME_CONNECTION_ACTIONS.ERROR,
          error:
            ctx.error?.message ||
            REALTIME_CONNECTION_ERROR_MESSAGES.SUBSCRIPTION,
        });
      });

      centrifuge.connect();
      subscription.subscribe();

      centrifugeRef.current = centrifuge;
      subscriptionRef.current = subscription;
    } catch (error) {
      recordAppError(error);
      dispatch({
        type: REALTIME_CONNECTION_ACTIONS.ERROR,
        error:
          error instanceof Error
            ? error.message
            : REALTIME_CONNECTION_ERROR_MESSAGES.CONNECTION_FAILED,
      });
    }
  }, [channel, handlePublication, maxReconnectDelayMs, reconnectDelayMs, url]);

  useEffect(() => {
    if (!enabled || !channel) {
      disconnect();
      return undefined;
    }

    connect();

    return () => {
      disconnect();
    };
  }, [channel, connect, disconnect, enabled]);

  return {
    connectionInfo,
    disconnect,
  };
}
